import React, { useEffect, useRef, useState } from 'react';
import { BasicInterpreter } from '../utils/BasicInterpreter';
import SoundManager from '../utils/SoundManager';

const WELCOME = [
  '    **** COMMODORE 64 BASIC V2 ****',
  ' 64K RAM SYSTEM  38911 BASIC BYTES FREE',
  '',
  'READY.',
];

const SAMPLE_PROGRAM = [
  '10 PRINT "HELLO FROM GB4K"',
  '20 FOR I = 1 TO 5',
  '30 PRINT I; "* "; I*2; "= "; I*I*2',
  '40 NEXT I',
  '50 PRINT "DONE."',
];

const BasicConsoleVisualization: React.FC = () => {
  const [history, setHistory] = useState<string[]>(WELCOME);
  const [input, setInput] = useState('');
  const [recallStack, setRecallStack] = useState<string[]>([]);
  const [recallIdx, setRecallIdx] = useState<number>(-1);
  const interpreterRef = useRef<BasicInterpreter>(new BasicInterpreter());
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [history]);

  const append = (lines: string[]) => {
    setHistory((prev) => {
      const combined = [...prev, ...lines];
      // Cap at ~500 lines to keep DOM small
      if (combined.length > 500) return combined.slice(-500);
      return combined;
    });
  };

  const handleSubmit = async (line: string) => {
    SoundManager.playSound('command');
    // Echo input (uppercase, like C64)
    append([`> ${line.toUpperCase()}`]);

    // If the interpreter is waiting for INPUT, resolve it instead
    if (interpreterRef.current.isWaitingForInput()) {
      interpreterRef.current.resolveInput(line);
      return;
    }

    try {
      const out = await interpreterRef.current.submitLine(line);
      const processed: string[] = [];
      for (const o of out) {
        if (o === '\x1bCLS') {
          // CLS sentinel — clear screen
          setHistory([]);
          return;
        }
        processed.push(o);
      }
      if (processed.length > 0) append(processed);
      append(['READY.']);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      append([`?${msg}`, 'READY.']);
      SoundManager.playSound('error');
    }
  };

  const handleKey = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const line = input;
      setInput('');
      if (line.trim()) {
        setRecallStack((prev) => [...prev, line]);
        setRecallIdx(-1);
      }
      await handleSubmit(line);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (recallStack.length === 0) return;
      const newIdx = recallIdx < 0 ? recallStack.length - 1 : Math.max(0, recallIdx - 1);
      setRecallIdx(newIdx);
      setInput(recallStack[newIdx]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (recallIdx < 0) return;
      const newIdx = recallIdx + 1;
      if (newIdx >= recallStack.length) {
        setRecallIdx(-1);
        setInput('');
      } else {
        setRecallIdx(newIdx);
        setInput(recallStack[newIdx]);
      }
    }
  };

  const loadSample = async () => {
    for (const line of SAMPLE_PROGRAM) {
      await interpreterRef.current.submitLine(line);
    }
    append(['(sample program loaded — type RUN)']);
  };

  return (
    <div className="w-full h-full flex flex-col text-[#5fe979] font-mono p-3 bg-black">
      <div className="flex items-center justify-between mb-2 text-xs opacity-60 flex-shrink-0">
        <span>BASIC V2 — RUN / LIST / NEW / SAVE / LOAD / CLS</span>
        <button
          onClick={loadSample}
          className="px-2 py-0.5 border border-[#5fe979] hover:bg-[#132a1b]"
        >
          LOAD SAMPLE
        </button>
      </div>
      <div
        ref={scrollRef}
        className="flex-grow overflow-y-auto border border-[var(--terminal-border)] p-2 bg-black"
        style={{ fontSize: '0.85rem', lineHeight: 1.3 }}
        onClick={() => inputRef.current?.focus()}
      >
        {history.map((line, i) => (
          <div key={i} className="whitespace-pre-wrap">
            {line === '' ? ' ' : line}
          </div>
        ))}
        <div className="flex items-center mt-1">
          <span>&gt;&nbsp;</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            className="flex-grow bg-transparent outline-none text-[#5fe979] font-mono uppercase"
            style={{ caretColor: '#5fe979' }}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div className="mt-2 text-xs opacity-50 flex-shrink-0">
        Type numbered lines (10 PRINT "X") to write programs. Type unnumbered
        commands (PRINT 2+3) for immediate mode. Try LOAD SAMPLE.
      </div>
    </div>
  );
};

export default BasicConsoleVisualization;
